import {
  Alert,
  Box,
  Card,
  Collapsible,
  HStack,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react'
import { isSortable } from '@dnd-kit/dom/sortable'
import { DragDropProvider, type DragEndEvent } from '@dnd-kit/react'
import { ChevronDown } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ConfirmDialog } from '@mangovoice/mango-ui'

import {
  useGetExtensionsLine,
  useGetExtensionsSharedVoicemailBox,
} from '../hooks/useDeviceReferenceData'
import { useDevicesI18n } from '../i18n'
import type {
  DeviceButtonPayload,
  DeviceModelItem,
} from '../types/device.types'
import {
  type ButtonFunctionOption,
  type DraftButton,
  getButtonFunctionOption,
  SIDECAR_ALLOWED_OPTION_VALUES,
} from './buttonConstants'
import {
  applyButtonFunctionChange,
  collectUsedParkingLotValues,
  createEmptyDraft,
  formatParkingLotLabel,
  isCordlessBaseModel,
  makeSortId,
  toDraft,
  toPayload,
} from './buttonDraftUtils'
import { SortableButtonRow } from './SortableButtonRow'

export {
  getDraftButtonFunctionAndExtensionId,
  normalizeIncomingButtonFunction,
} from './buttonDraftUtils'

export interface DeviceButtonGridProps {
  pbxId: number
  sidecarEnabled: boolean
  primaryExtensionId?: number | null
  model: DeviceModelItem | null
  buttons: DeviceButtonPayload[]
  onChange: (buttons: DeviceButtonPayload[]) => void
  lineButtonCount?: number
  onButtonValidationError?: (hasErrors: boolean) => void
}

export const DeviceButtonGrid = React.memo<DeviceButtonGridProps>(
  ({
    pbxId,
    sidecarEnabled,
    primaryExtensionId,
    model,
    buttons,
    onChange,
    lineButtonCount,
    onButtonValidationError,
  }) => {
    const { text } = useDevicesI18n()
    const { data: lineExtensions = [] } = useGetExtensionsLine(pbxId)
    const { data: voicemailExtensions = [] } =
      useGetExtensionsSharedVoicemailBox(pbxId)

    const presenceExtensions = useMemo(
      () =>
        primaryExtensionId
          ? lineExtensions.filter((ext) => ext.id !== primaryExtensionId)
          : lineExtensions,
      [lineExtensions, primaryExtensionId],
    )

    const translatedFunctionOptions: ButtonFunctionOption[] = useMemo(
      () => [
        { value: '', label: text('devices.buttons.functionBlank', 'Blank') },
        {
          value: 'line',
          label: text('devices.buttons.functionLine', 'Line'),
          type: 'line',
        },
        {
          value: 'rollover',
          label: text('devices.buttons.functionRollover', 'Rollover'),
          type: 'rollover',
        },
        {
          value: 'speed_dial',
          label: text('devices.buttons.functionSpeedDial', 'Speed Dial'),
          type: 'speed_dial',
        },
        {
          value: 'presence',
          label: text('devices.buttons.functionPresence', 'BLF / Presence'),
          type: 'presence',
        },
        {
          value: 'parking_slot',
          label: text('devices.buttons.functionParkingLot', 'Parking Lot'),
          type: 'parking_slot',
        },
        {
          value: 'voicemail',
          label: text(
            'devices.buttons.functionSharedVoicemail',
            'Shared Voicemail',
          ),
          type: 'voicemail',
        },
        { value: 'divider', label: '------------', disabled: true },
        {
          value: 'speed_dial__mango_support',
          label: text('devices.buttons.presetMangoSupport', 'Mango Support'),
          type: 'speed_dial',
          presetValue: '4354662646',
          presetLabel: text(
            'devices.buttons.presetLabelMangoSupport',
            'Mango Support',
          ),
        },
        {
          value: 'speed_dial__star10',
          label: text('devices.buttons.presetLoginQueue', 'Login Queue'),
          type: 'speed_dial',
          presetValue: '*10',
          presetLabel: text('devices.buttons.presetLabelLoginQ', 'Login Q'),
        },
        {
          value: 'speed_dial__star11',
          label: text('devices.buttons.presetLogoutQueue', 'Logout Queue'),
          type: 'speed_dial',
          presetValue: '*11',
          presetLabel: text('devices.buttons.presetLabelLogoutQ', 'Logout Q'),
        },
        {
          value: 'speed_dial__star999',
          label: text('devices.buttons.presetBlockCaller', 'Block Caller'),
          type: 'speed_dial',
          presetValue: '*999',
          presetLabel: text('devices.buttons.presetLabelBlock', 'Block'),
        },
        {
          value: 'speed_dial__star3',
          label: text('devices.buttons.presetOpenClose', 'Open/Close'),
          type: 'speed_dial',
          presetValue: '*3',
          presetLabel: text(
            'devices.buttons.presetLabelOpenClose',
            'Open/Close',
          ),
        },
        {
          value: 'dtmf__star20',
          label: text(
            'devices.buttons.presetPauseResumeRecording',
            'Pause/Resume Recording',
          ),
          type: 'speed_dial',
          presetValue: '*20',
          presetLabel: text(
            'devices.buttons.presetLabelPauseResume',
            'Pause/Resume',
          ),
        },
        {
          value: 'speed_dial__star98',
          label: text('devices.buttons.presetAlternateVM', 'Alternate VM'),
          type: 'speed_dial',
          presetValue: '*98',
          presetLabel: text('devices.buttons.presetLabelAltVM', 'Alt VM'),
        },
        {
          value: 'speed_dial__star60',
          label: text('devices.buttons.preset911History', '911 History'),
          type: 'speed_dial',
          presetValue: '*60',
          presetLabel: text('devices.buttons.presetLabel911Hist', '911 Hist'),
        },
      ],
      [text],
    )

    const effectiveLineButtonCount =
      lineButtonCount != null ? lineButtonCount : (model?.dss_key_count ?? 0)
    const showSidecar = sidecarEnabled && (model?.sidecar_supported ?? false)
    const sidecarCount = showSidecar ? (model?.sidecar_button_count ?? 0) : 0

    const existingLineMap = useMemo(
      () =>
        new Map(
          buttons
            .filter((b) => b.type === 'LineButton')
            .map((b) => [b.display_count, b] as const),
        ),
      [buttons],
    )

    const existingSidecarMap = useMemo(
      () =>
        new Map(
          buttons
            .filter((b) => b.type === 'SidecarButton')
            .map((b) => [b.display_count, b] as const),
        ),
      [buttons],
    )

    const lineSortIdsRef = useRef<string[]>([])
    const sidecarSortIdsRef = useRef<string[]>([])

    const lineDrafts: DraftButton[] = useMemo(() => {
      const drafts = Array.from(
        { length: effectiveLineButtonCount },
        (_, i) => {
          const pos = i + 1
          const existing = existingLineMap.get(pos)
          const draft = existing
            ? toDraft(existing)
            : createEmptyDraft('LineButton', pos)
          if (pos === 1 && !draft.button_function) {
            draft.button_function = 'line'
          }
          if (pos === 1) {
            draft.extension_id = primaryExtensionId ?? ''
            draft.button_value = primaryExtensionId
              ? String(primaryExtensionId)
              : ''
          }
          return draft
        },
      )
      const prev = lineSortIdsRef.current
      const ids = drafts.map((_, i) => prev[i] ?? makeSortId())
      lineSortIdsRef.current = ids
      return drafts.map((d, i) => ({ ...d, _sortId: ids[i] }))
    }, [effectiveLineButtonCount, existingLineMap, primaryExtensionId])

    const sidecarDrafts: DraftButton[] = useMemo(() => {
      const drafts = Array.from({ length: sidecarCount }, (_, i) => {
        const pos = i + 1
        const existing = existingSidecarMap.get(pos)
        return existing
          ? toDraft(existing)
          : createEmptyDraft('SidecarButton', pos)
      })
      const prev = sidecarSortIdsRef.current
      const ids = drafts.map((_, i) => prev[i] ?? makeSortId())
      sidecarSortIdsRef.current = ids
      return drafts.map((d, i) => ({ ...d, _sortId: ids[i] }))
    }, [sidecarCount, existingSidecarMap])

    const duplicatePresenceExtIds = useMemo(() => {
      const counts = new Map<number, number>()
      for (const d of [...lineDrafts, ...sidecarDrafts]) {
        const option = getButtonFunctionOption(d.button_function)
        if (option?.type !== 'presence' || d.extension_id === '') continue
        const id = Number(d.extension_id)
        counts.set(id, (counts.get(id) ?? 0) + 1)
      }
      return new Set(
        [...counts.entries()].filter(([, c]) => c > 1).map(([id]) => id),
      )
    }, [lineDrafts, sidecarDrafts])

    const duplicateLineExtIds = useMemo(() => {
      const counts = new Map<number, number>()
      for (const d of [...lineDrafts, ...sidecarDrafts]) {
        const option = getButtonFunctionOption(d.button_function)
        if (option?.type !== 'line' || d.extension_id === '') continue
        const id = Number(d.extension_id)
        counts.set(id, (counts.get(id) ?? 0) + 1)
      }
      return new Set(
        [...counts.entries()].filter(([, c]) => c > 1).map(([id]) => id),
      )
    }, [lineDrafts, sidecarDrafts])

    // BLF/Presence, Voicemail, and non-primary Line buttons require an
    // extension. Without one the API drops the button (so a "ghost" presence
    // button silently disappears) or worse, falls back to the device's own
    // primary line. Track the slots so we can error them in the UI and block
    // submission until a value is selected.
    const missingExtensionSortIds = useMemo(() => {
      const ids = new Set<string>()
      for (const drafts of [lineDrafts, sidecarDrafts]) {
        drafts.forEach((d) => {
          const fnType = getButtonFunctionOption(d.button_function)?.type
          // The primary line button is special-cased in sanitizeButton (it
          // pulls the primary extension from the form), so we skip it here
          // and only validate explicit line buttons in non-primary slots.
          const isPrimaryLine =
            fnType === 'line' &&
            d.type === 'LineButton' &&
            d.display_count === 1
          const needsExtension =
            fnType === 'presence' ||
            fnType === 'voicemail' ||
            (fnType === 'line' && !isPrimaryLine)
          if (!needsExtension) return
          if (d.extension_id === '') ids.add(d._sortId)
        })
      }
      return ids
    }, [lineDrafts, sidecarDrafts])

    useEffect(() => {
      onButtonValidationError?.(
        duplicatePresenceExtIds.size > 0 ||
          duplicateLineExtIds.size > 0 ||
          missingExtensionSortIds.size > 0,
      )
    }, [
      duplicatePresenceExtIds,
      duplicateLineExtIds,
      missingExtensionSortIds,
      onButtonValidationError,
    ])

    const showMultipleLineWarning = useMemo(() => {
      if (isCordlessBaseModel(model?.name)) return false
      const lineCount = [...lineDrafts, ...sidecarDrafts].filter(
        (d) => getButtonFunctionOption(d.button_function)?.type === 'line',
      ).length
      return lineCount > 1
    }, [lineDrafts, sidecarDrafts, model?.name])

    const [multipleLineDialogOpen, setMultipleLineDialogOpen] = useState(false)
    const prevShowWarningRef = useRef(showMultipleLineWarning)
    const preWarningButtonsRef = useRef<DeviceButtonPayload[]>(buttons)

    useEffect(() => {
      if (showMultipleLineWarning && !prevShowWarningRef.current) {
        setMultipleLineDialogOpen(true)
      } else {
        preWarningButtonsRef.current = buttons
      }
      prevShowWarningRef.current = showMultipleLineWarning
    }, [showMultipleLineWarning, buttons])

    const emitChange = useCallback(
      (newLine: DraftButton[], newSidecar: DraftButton[]) => {
        onChange([...newLine.map(toPayload), ...newSidecar.map(toPayload)])
      },
      [onChange],
    )

    const updateDraft = useCallback(
      (
        type: 'LineButton' | 'SidecarButton',
        index: number,
        field: keyof DraftButton,
        value: string | number,
      ) => {
        const isLine = type === 'LineButton'
        const drafts = isLine ? [...lineDrafts] : [...sidecarDrafts]
        if (!drafts[index]) return

        const updated =
          field === 'button_function'
            ? applyButtonFunctionChange(drafts[index], value as string, {
                presenceExtensions,
                usedParkingLotValues: collectUsedParkingLotValues(
                  lineDrafts,
                  sidecarDrafts,
                  {
                    type: isLine ? 'LineButton' : 'SidecarButton',
                    index,
                  },
                ),
              })
            : { ...drafts[index], [field]: value }

        if (field === 'extension_id') {
          const option = getButtonFunctionOption(updated.button_function)
          const fnType = option?.type
          if (value === '') {
            // Clear the cached extension id from button_value too — otherwise
            // it persists from the previous selection and the device ends up
            // calling that old extension even after the user picked "-- None --".
            updated.button_label = ''
            updated.button_value = ''
          } else if (fnType === 'line') {
            updated.button_label = `Line ${updated.display_count}`
          } else if (fnType === 'presence') {
            const ext = presenceExtensions.find((e) => e.id === Number(value))
            if (ext) updated.button_label = ext.name
          } else if (fnType === 'voicemail') {
            const ext = voicemailExtensions.find((e) => e.id === Number(value))
            if (ext) updated.button_label = 'VM ' + ext.number
          }
        }

        if (field === 'button_value') {
          const option = getButtonFunctionOption(updated.button_function)
          if (option?.type === 'parking_slot' && value) {
            const lotValue = Number(value)
            updated.button_label = formatParkingLotLabel(lotValue)
          }
        }

        drafts[index] = updated
        if (isLine) {
          emitChange(drafts, sidecarDrafts)
        } else {
          emitChange(lineDrafts, drafts)
        }
      },
      [
        lineDrafts,
        sidecarDrafts,
        emitChange,
        presenceExtensions,
        voicemailExtensions,
      ],
    )

    const handleReorder = useCallback(
      (
        type: 'LineButton' | 'SidecarButton',
        oldIndex: number,
        newIndex: number,
      ) => {
        const isLine = type === 'LineButton'
        if (isLine && newIndex === 0) return
        const drafts = isLine ? [...lineDrafts] : [...sidecarDrafts]
        const [moved] = drafts.splice(oldIndex, 1)
        drafts.splice(newIndex, 0, moved)
        const renumbered = drafts.map((d, i) => ({
          ...d,
          display_count: i + 1,
        }))

        const idsRef = isLine ? lineSortIdsRef : sidecarSortIdsRef
        const ids = [...idsRef.current]
        const [movedId] = ids.splice(oldIndex, 1)
        ids.splice(newIndex, 0, movedId)
        idsRef.current = ids

        if (isLine) {
          emitChange(renumbered, sidecarDrafts)
        } else {
          emitChange(lineDrafts, renumbered)
        }
      },
      [lineDrafts, sidecarDrafts, emitChange],
    )

    const handleLineDragEnd = useCallback(
      (event: DragEndEvent) => {
        if (event.canceled) return
        const { source } = event.operation
        if (!source || !isSortable(source)) return
        if (source.initialIndex === source.index) return
        handleReorder('LineButton', source.initialIndex, source.index)
      },
      [handleReorder],
    )

    const handleSidecarDragEnd = useCallback(
      (event: DragEndEvent) => {
        if (event.canceled) return
        const { source } = event.operation
        if (!source || !isSortable(source)) return
        if (source.initialIndex === source.index) return
        handleReorder('SidecarButton', source.initialIndex, source.index)
      },
      [handleReorder],
    )

    const sidecarFunctionOptions = useMemo(
      () =>
        translatedFunctionOptions.filter((option) =>
          SIDECAR_ALLOWED_OPTION_VALUES.has(option.value),
        ),
      [translatedFunctionOptions],
    )

    const renderButtonTable = (
      title: string,
      type: 'LineButton' | 'SidecarButton',
      drafts: DraftButton[],
      onDragEnd: (event: DragEndEvent) => void,
      helperText?: string,
    ): React.ReactNode => (
      <Collapsible.Root defaultOpen>
        <Card.Root>
          <Collapsible.Trigger asChild>
            <Card.Header p={6} cursor="pointer" userSelect="none">
              <HStack justify="space-between">
                <Card.Title>{title}</Card.Title>
                <Collapsible.Indicator
                  transition="transform 0.2s"
                  _open={{ transform: 'rotate(-180deg)' }}
                >
                  <ChevronDown size={18} />
                </Collapsible.Indicator>
              </HStack>
              {helperText && (
                <Text color="fg.muted" fontSize="sm" mt={1}>
                  {helperText}
                </Text>
              )}
            </Card.Header>
          </Collapsible.Trigger>
          <Collapsible.Content>
            <Card.Body p={6} pt={0}>
              {drafts.length === 0 ? (
                <Text color="fg.muted">
                  {model
                    ? text(
                        'devices.buttons.noSlotsForModel',
                        'No button slots available for this model.',
                      )
                    : text(
                        'devices.buttons.selectModelForSlots',
                        'Select a device model to see button slots.',
                      )}
                </Text>
              ) : (
                <DragDropProvider onDragEnd={onDragEnd}>
                  <Table.Root variant="outline" size="sm">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader px={1} py={2} w="36px" />
                        <Table.ColumnHeader px={3} py={2} w="50px">
                          {text('devices.buttons.columnNumber', '#')}
                        </Table.ColumnHeader>
                        <Table.ColumnHeader px={3} py={2} w="150px">
                          {text('devices.buttons.columnFunction', 'Function')}
                        </Table.ColumnHeader>
                        <Table.ColumnHeader px={3} py={2}>
                          {text('devices.buttons.columnValue', 'Value')}
                        </Table.ColumnHeader>
                        <Table.ColumnHeader px={3} py={2} w="200px">
                          {text('devices.buttons.columnLabel', 'Label')}
                        </Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {drafts.map((d, idx) => (
                        <SortableButtonRow
                          key={d._sortId}
                          draft={d}
                          idx={idx}
                          type={type}
                          isFirstLineRow={
                            type === 'LineButton' && d.display_count === 1
                          }
                          isSortingDisabled={type === 'LineButton' && idx === 0}
                          functionOptions={
                            type === 'SidecarButton'
                              ? sidecarFunctionOptions
                              : translatedFunctionOptions
                          }
                          duplicatePresenceExtIds={duplicatePresenceExtIds}
                          duplicateLineExtIds={duplicateLineExtIds}
                          missingExtension={missingExtensionSortIds.has(
                            d._sortId,
                          )}
                          lineExtensions={lineExtensions}
                          presenceExtensions={presenceExtensions}
                          voicemailExtensions={voicemailExtensions}
                          text={text}
                          updateDraft={updateDraft}
                        />
                      ))}
                    </Table.Body>
                  </Table.Root>
                </DragDropProvider>
              )}
            </Card.Body>
          </Collapsible.Content>
        </Card.Root>
      </Collapsible.Root>
    )

    return (
      <VStack align="stretch" gap={6}>
        {renderButtonTable(
          text('devices.buttons.linesAndButtons', 'Lines & Buttons'),
          'LineButton',
          lineDrafts,
          handleLineDragEnd,
          model
            ? text(
                'devices.buttons.lineButtonHelper',
                '{{count}} button slot for {{modelName}}. Row 1 is always Line and follows Primary Line Extension.',
                {
                  count: effectiveLineButtonCount,
                  modelName: model.name,
                },
              )
            : undefined,
        )}
        {showMultipleLineWarning && (
          <Alert.Root status="warning">
            <Alert.Indicator />
            <Box>
              <Text fontWeight="medium" fontSize="sm">
                {text(
                  'devices.buttons.multipleLineWarningTitle',
                  'Multiple Line buttons detected',
                )}
              </Text>
              <Text fontSize="sm">
                {text(
                  'devices.buttons.multipleLineWarning',
                  'Adding multiple Line buttons may cause undesired results. Make sure the extension you select is not assigned to another Line button and is not configured on any other device.',
                )}
              </Text>
            </Box>
          </Alert.Root>
        )}
        <ConfirmDialog
          open={multipleLineDialogOpen}
          onClose={() => {
            onChange(preWarningButtonsRef.current)
            setMultipleLineDialogOpen(false)
          }}
          title={text(
            'devices.buttons.multipleLineWarningTitle',
            'Multiple Line buttons detected',
          )}
          description={text(
            'devices.buttons.multipleLineWarning',
            'Adding multiple Line buttons may cause undesired results. Make sure the extension you select is not assigned to another Line button and is not configured on any other device.',
          )}
          confirmLabel={text(
            'devices.buttons.multipleLineWarningProceed',
            'Proceed',
          )}
          cancelLabel={text(
            'devices.buttons.multipleLineWarningCancel',
            'Cancel',
          )}
          onConfirm={() => setMultipleLineDialogOpen(false)}
          confirmColorPalette="orange"
        />
        {showSidecar &&
          sidecarCount > 0 &&
          renderButtonTable(
            text('devices.buttons.sidecarButtons', 'Sidecar Buttons'),
            'SidecarButton',
            sidecarDrafts,
            handleSidecarDragEnd,
            text(
              'devices.buttons.sidecarButtonHelper',
              '{{count}} sidecar slot.',
              { count: sidecarCount },
            ),
          )}
      </VStack>
    )
  },
)

DeviceButtonGrid.displayName = 'DeviceButtonGrid'
